import React from 'react';

import { Button } from '@trello/nachos/button';
import { usePopover, Popover } from '@trello/nachos/popover';
import { DownIcon } from '@trello/nachos/icons/down';
import { CheckIcon } from '@trello/nachos/icons/check';

import { forTemplate } from 'app/common/lib/util/i18n';

import { GroupByOption, ZoomLevel } from './types';

import styles from './ViewHeaderDropdown.less';

const format = forTemplate('views');

type DropdownOption = ZoomLevel | GroupByOption;

interface ViewHeaderDropdownProps {
  options: DropdownOption[];
  currentOption: string;
  onChangeOption: (item: DropdownOption) => void;
  sendTrackingEvent: (selectedValue: DropdownOption) => void;
  hideUnscheduledPopover?: () => void;
}

export const ViewHeaderDropdown = ({
  options,
  currentOption,
  onChangeOption,
  sendTrackingEvent,
  hideUnscheduledPopover,
}: ViewHeaderDropdownProps) => {
  const {
    toggle,
    hide,
    triggerRef,
    popoverProps,
  } = usePopover<HTMLButtonElement>();

  const optionAction = (option: DropdownOption) => {
    hide();
    sendTrackingEvent(option);
    hideUnscheduledPopover && hideUnscheduledPopover();
    onChangeOption(option);
  };
  const renderCheck = (option: DropdownOption) => {
    if (option === currentOption) {
      return (
        <CheckIcon
          size="small"
          dangerous_className={styles.headerDropdownCheck}
        />
      );
    }
  };

  const onKeyUp = (e: React.KeyboardEvent<Element>, option: DropdownOption) => {
    switch (e.which) {
      case 32: // space bar
        e.preventDefault(); // stop the document from scrolling
      // fallthrough
      case 13: // enter
        optionAction(option);
        break;
      default:
        break;
    }
  };

  return (
    <div className={styles.headerSelect}>
      <Button
        className={styles.headerDropdownButton}
        onClick={toggle}
        ref={triggerRef}
      >
        {format(currentOption)}
        <DownIcon
          size="small"
          dangerous_className={styles.headerDropdownButtonIcon}
        />
      </Button>
      <Popover {...popoverProps} dangerous_width={160}>
        <ul className="pop-over-list inline-check">
          {options.map((option) => {
            return (
              <li key={option}>
                <a
                  onClick={() => optionAction(option)}
                  onKeyUp={(e) => onKeyUp(e, option)}
                  role="button"
                  className="pop-over-list-item"
                  tabIndex={0}
                >
                  {format(option)}
                  {renderCheck(option)}
                </a>
              </li>
            );
          })}
        </ul>
      </Popover>
    </div>
  );
};
